/**
 * 建筑系统类型定义
 * 用于管理建筑本体、建筑运行态以及城市对建筑的覆盖配置
 */

import { z } from 'zod';

// ============================================
// 建筑类型定义
// ============================================

/**
 * 建筑类型枚举
 */
export const BuildingTypeSchema = z.enum([
  'tavern', // 酒馆
  'inn', // 旅店
  'shop', // 商店/市场
  'guild', // 公会 (冒险者/商会)
  'temple', // 神庙/教会
  'castle', // 城堡/领主府
  'plaza', // 广场/城市中心
  'city_exit', // 城门/出城点
]);

export type BuildingType = z.infer<typeof BuildingTypeSchema>;

export const BuildingSceneRoleSchema = z.enum(['owner', 'staff', 'guest']);

export type BuildingSceneRole = z.infer<typeof BuildingSceneRoleSchema>;

export const BuildingSceneRulesSchema = z.object({
  sceneRoles: z.array(BuildingSceneRoleSchema).optional(), // 仅声明该建筑支持哪些室内场景角色
});

export type BuildingSceneRules = z.infer<typeof BuildingSceneRulesSchema>;

// ============================================
// 建筑运行态定义
// ============================================

export const BuildingCenterSchema = z.tuple([z.number(), z.number()]);

export type BuildingCenter = z.infer<typeof BuildingCenterSchema>;

export const BuildingSceneBindingSchema = z.object({
  ownerCharacterId: z.string().optional(),
  sceneCharacterIds: z.array(z.string()).default([]),
  source: z.string().optional(),
});

export type BuildingSceneBinding = z.infer<typeof BuildingSceneBindingSchema>;

export function parseBuildingSceneBinding(value: unknown): BuildingSceneBinding {
  const rawBinding = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  const ownerCharacterId =
    typeof rawBinding.ownerCharacterId === 'string' ? rawBinding.ownerCharacterId.trim() : undefined;
  const sceneCharacterIds = Array.isArray(rawBinding.sceneCharacterIds)
    ? rawBinding.sceneCharacterIds
        .filter((item): item is string => typeof item === 'string')
        .map(item => item.trim())
        .filter(Boolean)
    : [];
  const source = typeof rawBinding.source === 'string' ? rawBinding.source.trim() : undefined;

  return BuildingSceneBindingSchema.parse({
    ownerCharacterId: ownerCharacterId || undefined,
    sceneCharacterIds,
    source: source || undefined,
  });
}

/**
 * 建筑信息 Schema
 * 存储城市地图中已被发现或固化的建筑状态
 */
export const KnownBuildingSchema = z.object({
  id: z.string(), // 建筑唯一标识 (对应地图数据中的 Polygon ID)
  type: BuildingTypeSchema, // 建筑类型
  name: z.string(), // 建筑名称 (如 "生锈的船锚酒馆")
  description: z.string().optional(), // 场景描述 (AI 生成)
  functionId: z.string().optional(), // 关联的功能组件 ID (如具体的商店/NPC ID)
  isVisited: z.boolean().default(false), // 是否已访问过
  center: BuildingCenterSchema.optional(), // 建筑中心点，供交互、寻路与地图展示复用
  entryPoint: BuildingCenterSchema.optional(), // 建筑户外交互点，通常位于门口或贴近道路的一侧
  layoutSource: z.string().optional(), // 建筑布局识别来源（如 prism、district_castle、square）
  sceneRules: BuildingSceneRulesSchema.optional(), // 建筑自身的场景角色声明
  sceneBinding: BuildingSceneBindingSchema.optional(), // 建筑的手写/外部场景绑定；系统派生结果不再写回这里
  metadata: z.record(z.string(), z.any()).optional(), // 少量非核心扩展信息（如特殊交互标记）
});

export type KnownBuilding = z.infer<typeof KnownBuildingSchema>;

// ============================================
// 建筑静态设定
// ============================================

/**
 * 建筑本体定义
 * 用于承载可复用或城市专属的建筑名字与描述。
 */
export interface BuildingProfile {
  id: string;
  type: BuildingType;
  name: string;
  description?: string;
  sceneRules?: BuildingSceneRules;
}

/**
 * 城市建筑覆盖项
 * 当前最小方案只支持按建筑类型覆盖到单个已知建筑。
 * 人物关系不再放在建筑侧，由人物自身的 presence.relationships 表达。
 */
export interface CityBuildingOverride {
  matchType: BuildingType;
  buildingProfileId: string;
}

/**
 * 某座城市的建筑覆盖集合
 */
export interface CityBuildingOverrides {
  cityId: number;
  buildings: CityBuildingOverride[];
}
